import type { PickupPoint } from '~/hooks/usePickupZones';
import { cartStore, orderStore } from '~/stores';

import type { AddressMode } from './hooks/useDeliveryPickupAddress';

/**
 * Whether confirming `point` moves the order to a different store, invalidating
 * the cart built against the current one. Single source of truth for the
 * warning shown under the selected point and for the cart reset the confirm
 * then performs, so the two can never disagree.
 *
 * `mode` is the fulfilment the user is about to commit, not the one currently
 * active — deliberately, so switching from courier delivery to self-collection
 * at another store is covered too. `useDeliveryMethodSwitch`'s own
 * confirmation does not reach that case: it compares the courier and pickup
 * warehouses of the CURRENT address, which says nothing about the store being
 * picked here. Nor can the two prompt twice — that modal empties the cart on
 * confirm, so the `cart.length` check below is already false afterwards.
 *
 * Without a committed ETA there is no current store to move away from.
 */
export const isPickupStoreChanging = (
  mode: AddressMode,
  point: PickupPoint | null,
): boolean => {
  const currentWarehouseCode = orderStore.etaCalculation?.warehouse.code;

  return (
    mode === 'pickup' &&
    !!point &&
    !!currentWarehouseCode &&
    cartStore.cart.length > 0 &&
    point.warehouseCode !== currentWarehouseCode
  );
};
